import { createResourceError } from '@jira/router-resources__utils/src/createResourceError.tsx';
import { fg } from '@atlassian/jira-feature-gating';

import { createResource, type RouteResource } from '@atlassian/react-resource-router';

import { fetchAvpSemanticModel } from './fetchAvpSemanticModel.tsx';
import type { CreateSemanticModelResourceArgs } from './CreateSemanticModelResourceArgs.tsx';

const RESOURCE_TYPE_AVP_SEMANTIC_MODEL = 'AVP_SEMANTIC_MODEL' as const;

export const createSemanticModelResource = (
	args: CreateSemanticModelResourceArgs,
	// oxlint-disable-next-line @typescript-eslint/no-explicit-any
): RouteResource<any> => {
	return createResource({
		type: `${RESOURCE_TYPE_AVP_SEMANTIC_MODEL}_${[Object.values(args)].join('_')}`,
		getKey: (_, { tenantContext: { activationId, cloudId } }) =>
			JSON.stringify({
				...args,
				products: typeof args.products === 'function' ? args.products() : args.products,
				subProducts: typeof args.subProducts === 'function' ? args.subProducts() : args.subProducts,
				location: typeof args.location === 'function' ? args.location() : args.location,
				activationId,
				cloudId,
			}),
		isBrowserOnly: true,
		getData: async (_, { tenantContext: { activationId, cloudId, locale } }) => {
			const __startTime = fg('platform_avp_custom-fetch-spans_dashboard')
				? performance.now()
				: null;
			try {
				if (!activationId) {
					throw new Error('Route config is missing activation ID');
				}
				if (!cloudId) {
					throw new Error('Route config is missing cloud ID');
				}
				const userInfo = {
					localTimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
					locale,
				};
				const data = await fetchAvpSemanticModel({
					activationId,
					cloudId,
					userInfo,
					...args,
				});
				return __startTime
					? { ...data, __metadata: { startTime: __startTime, endTime: performance.now() } }
					: data;
			} catch (error) {
				throw createResourceError(
					{
						component: 'spa.view.routes.viz-platform.getData',
						message: 'Error fetching semantic model',
						name: 'ResourceError',
					},
					error,
				);
			}
		},
		maxAge: 60_000,
	});
};
